/**
 * Shared frontend contracts for the Video Director workspace.
 * Proposal generation is backed by VIDEO_DIRECTOR_PROPOSAL Skill runs
 * (`/video-director/*` APIs). DEV fixtures in `videoDirectorFixtures.ts`
 * remain available only for local card rendering experiments.
 */

/** MODE A (virtual_ip) vs MODE B (independent) — spec §二. Two
 * independent entry points; neither secretly implies the other. */
export type VideoCreationMode = 'virtual_ip' | 'independent'

/** Spec §七 — reference asset roles. Only `reference_image` /
 * `reference_video` are wired to a real upload path this round; the
 * others are reserved so a future round can add dedicated pickers
 * without changing this union's shape at the call sites. */
export type ReferenceAssetRole =
  | 'reference_image'
  | 'reference_video'
  | 'character_reference'
  | 'scene_reference'
  | 'action_reference'
  | 'style_reference'

export interface ReferenceAssetRef {
  asset_id: string
  role: ReferenceAssetRole
  media_type: 'image' | 'video'
  filename: string
  preview_url?: string
}

/** Spec §三 — real fields only. This workspace's Virtual IP surface
 * today is a single Positioning Draft (`VirtualIpDraft`), not a
 * multi-IP catalog with name/avatar/version — see
 * `VirtualIpContextCard.tsx` for the honest real-data mapping. */
export interface VirtualIpContextRef {
  draft_id: string
  virtual_ip_version: string | null
  has_intake: boolean
}

/** Spec §十一 — `video_director_concept_v1`. Structured sections, never
 * a single Markdown blob. Exact final schema is intentionally NOT
 * locked this round (spec: "本轮不要擅自锁死 backend schema") — this is
 * the frontend's own rendering contract, shaped to be a superset the
 * real Harness output can populate later. */
export interface VideoDirectorConceptV1 {
  concept_id: string
  label: 'A' | 'B' | 'C'
  title: string
  tagline: string
  summary: string
  creative_direction: string
  story_concept: string
  performance_direction: string
  action_design: string
  camera_direction: string
  pacing: string
  scene_direction: string
  lighting_direction: string
  visual_style: string
  identity_constraints: string
  continuity_constraints: string
  platform_fit: string
  estimated_duration_seconds: number
  /** Canonical Skill fields (optional for DEV fixtures). */
  estimated_shot_count?: number
  generation_strategy?: string
  style_tags?: string[]
  shot_outline?: string
  shots?: VideoDirectorConceptShotV1[]
  batch_id?: string
}

export interface VideoDirectorConceptShotV1 {
  shot_index?: number
  duration_seconds?: number
  shot_type?: string
  framing?: string
  camera_movement?: string
  subject_action?: string
  environment_action?: string
  visual_description?: string
  transition?: string
  audio_note?: string
  reference_usage?: string
  [key: string]: unknown
}

export type DirectorHarnessStatus =
  | 'idle'
  | 'not_connected'
  | 'generating'
  | 'ready'
  | 'error'
  | 'needs_regeneration'

/** Spec §十八/§十九 — real, backend-validated basic parameters. */
export interface VideoParametersV1 {
  aspect_ratio: string
  quality_tier: string
  duration_seconds: number
  frame_rate: number
  generation_count: number
  seed_mode: 'random' | 'fixed'
  seed: number | null
  image_strength: number
  first_strength: number
  last_strength: number
}

/** Spec §二十五 — draft persistence contract. Real fields, `PARTIAL`
 * persistence this round (browser `sessionStorage`, never a fake
 * "saved to server" claim) — see `videoDirectorDraft.ts`. */
export interface VideoDirectorDraftV1 {
  schema_version: 'video_director_draft_v1'
  creation_mode: VideoCreationMode
  virtual_ip_ref: VirtualIpContextRef | null
  user_intent: string
  reference_assets: ReferenceAssetRef[]
  video_parameters: VideoParametersV1
  selected_director_concept_id: string | null
  /** Server-side VIDEO_DIRECTOR_PROPOSAL session id (canvas-reusable). */
  director_session_id: string | null
  updated_at: string
}

export const USER_INTENT_MAX_LENGTH = 2000

export const DEFAULT_VIDEO_PARAMETERS_V1: VideoParametersV1 = {
  aspect_ratio: '9:16',
  quality_tier: '720P',
  duration_seconds: 10,
  frame_rate: 24,
  generation_count: 1,
  seed_mode: 'random',
  seed: null,
  image_strength: 0.6,
  first_strength: 0.6,
  last_strength: 0.6,
}
